// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Close } from '@mui/icons-material';
import { Box, Dialog, IconButton, Toolbar, Typography } from '@mui/material';
import { TextInputLabel } from '../../../components/Form/TextInput';
import PartyDisplay from '../../../components/PartyDisplay';
import { StateCell, Status } from '../../../components/StateCell';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { fmt } from '../../../utils/common';
import { useGetCollateralState } from '../../../utils/useGetCollateralState';
import { CollateralAgreementRow } from '../collaterals/types';

interface AgreementDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedRow?: CollateralAgreementRow;
}
export const AgreementDetailsDialog: React.FC<AgreementDetailsDialogProps> = ({
  open,
  onClose,
  selectedRow,
}) => {
  const party = useModuleParty();
  const getCollateralState = useGetCollateralState();
  if (!selectedRow) {
    return null;
  }
  const collateralState = getCollateralState({
    operator: selectedRow.payload.operator,
    partyA: selectedRow.payload.requestor,
    partyB: selectedRow.payload.counterparty,
    id: selectedRow.payload.id,
  });
  return (
    <Dialog open={open} onClose={onClose}>
      <Toolbar>
        <Typography variant="h4" fontWeight="bold">
          Agreement detail
        </Typography>
        <IconButton onClick={onClose} sx={{ ml: 'auto' }}>
          <Close fontSize="small" color="action" />
        </IconButton>
      </Toolbar>
      <Box
        sx={{
          overflow: 'hidden',
          pl: 3,
          pb: 3,
          pr: 3,
          minWidth: 600,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <TextInputLabel label="State" />
          {selectedRow.state && <StateCell state={selectedRow.state as Status} />}
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextInputLabel label="Agreement Id" />
          <Typography variant="body2">{selectedRow?.payload.id}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextInputLabel label="Party A" />
          {selectedRow.state && (
            <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <PartyDisplay party={selectedRow.payload.requestor} />
            </Typography>
          )}
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextInputLabel label="Party B" />
          {selectedRow.state && (
            <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <PartyDisplay party={selectedRow.payload.counterparty} />
            </Typography>
          )}
        </Box>
        <Box sx={{ mb: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <TextInputLabel label="Eligible Collateral" />
          </Box>
          <Box sx={{ flex: 1, whiteSpace: 'nowrap' }}>
            <TextInputLabel label="Total Settled balance" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextInputLabel label="Admin Id" />
          </Box>
        </Box>

        {selectedRow.payload.terms.eligibleCollateral.eligibleInstruments.map((i) => {
          const amount =
            collateralState?.payload.collateralPositions.find(
              (c) =>
                c.instrument.id === i.instrument.id && c.instrument.admin === i.instrument.admin,
            )?.amount || 0;
          const isZeroAmount = amount === 0;
          const formattedAmount = fmt(amount, 2);
          const isPledgor = collateralState?.payload.collateralPositions?.[0]?.pledgor === party;
          const directionSign = (
            <Typography
              sx={{
                color: (t) => (isPledgor ? undefined : t.palette.success.main),
              }}
            >
              {isPledgor ? String.fromCodePoint(0x2212) : '+'}
            </Typography>
          );

          return (
            <Box
              key={i.instrument.id}
              sx={{ mb: 0, display: 'flex', flexDirection: 'row', alignItems: 'center' }}
            >
              <Box sx={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'row' }}>
                <Typography variant="body2">{i.instrument.id}</Typography>
              </Box>
              <Box
                sx={{
                  overflow: 'hidden',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                {isZeroAmount ? null : directionSign}
                <Typography
                  sx={{
                    color: isZeroAmount
                      ? null
                      : (t) => (isPledgor ? undefined : t.palette.success.main),
                  }}
                  variant="body2"
                >
                  {formattedAmount}
                </Typography>
              </Box>
              <Box sx={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'row' }}>
                <Typography
                  variant="body2"
                  sx={{
                    pointer: 'cursor',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <PartyDisplay party={i.instrument.admin} />
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Dialog>
  );
};
